import { useEffect, useState } from 'react';
import { Modal, ModalBody } from 'reactstrap';
import { useRecoilValue } from 'recoil';
import { BiCheckCircle } from 'react-icons/bi'
import { toast } from 'react-hot-toast';
import { map } from 'lodash';

import { useCollaboratorsActions } from '../../../../services/actions/collaborators.actions';
import { invitationAtom } from '../../../../services/atoms/collaborators.atoms';
import { useAuthActions } from '../../../../services/actions/auth.actions';
import { ButtonLoading } from '../../../../components/buttons/button-loading';
import { ERROR_MESSAGES, AVAILABLE_TESTS } from '../../../../helpers/constants';
import { TestTypeService } from '../../../../utils/test-type-registry';
import CompetencyModeSelector, {
  isCompetencySelectionValid,
  selectedCompetencesForPayload,
} from '../../selection-process/components/competency-mode-selector';

/**
 * Switch temporal: oculta la selección de test (textos de ayuda + checkboxes) y deja
 * el modal mostrando sólo el modo de evaluación.
 *
 * Con el bloque oculto no hay nada que fije `targetTest`, así que se preselecciona el
 * Test Growme unificado; sin eso el envío se iría sin `testID`. Poner en `true` para
 * volver al modal completo.
 */
const SHOW_TEST_SELECTION = false;

const unifiedTest = AVAILABLE_TESTS.find(
  (test) => TestTypeService.getTestConfig(test.code)?.category === 'unified'
);

const invitationSendText = 'La invitación se ha enviado de manera satisfactoria.';
const invitationInfoText = (rest) => `Selecciona el test que deseas enviar  a ${Array.isArray(rest) ? `los candidatos ${rest}` : rest} Luego, presiona 'confirmar' para completar envío.`;

const InvitationModal = (props) => {
  const { open, toggle, checkedItems, allItems, onTestSent } = props;
  const { sendInvitation } = useCollaboratorsActions();
  const { refreshToken } = useAuthActions();
  const invitationResolver = useRecoilValue(invitationAtom);
  const [send, setSend] = useState(false);
  const [targetTest, setTargetTest] = useState(
    SHOW_TEST_SELECTION ? '' : (unifiedTest?.id ?? '')
  );
  const [competencyMode, setCompetencyMode] = useState(null);
  const [selectedCompetences, setSelectedCompetences] = useState([]);

  // El estado vive acá y no en el `Modal` de reactstrap, que sólo se oculta: sin
  // este reset, reabrir el modal conservaría el modo del envío anterior.
  useEffect(() => {
    if (open) {
      setCompetencyMode(null);
      setSelectedCompetences([]);
    }
  }, [open]);

  // El modo de evaluación sólo aplica al Test Growme unificado: los demás no tienen
  // secciones de competencias que elegir.
  const isUnifiedTest = TestTypeService.isTestCategory({ id: targetTest }, 'unified');

  // Cambiar de test resetea el modo, para no arrastrar una selección hecha para otro
  // test ni mandarla en el payload de uno que la ignora.
  const handleSelectTest = (testId) => {
    setTargetTest(testId);
    setCompetencyMode(null);
    setSelectedCompetences([]);
  };

  const handleToggle = () => {
    toggle('invitation');
    setTimeout(() => setSend(false), 500);
  }

  const getItemId = (items) => map(items || [], (e) => (e.id))

  const handleSubmit = (e) => {
    e.preventDefault();
    sendInvitation({
      "testID": `${targetTest}`,
      "collaboratorIDs": checkedItems?.length ? getItemId(checkedItems) : getItemId(allItems),
      // La selección aplica al lote completo de colaboradores del envío.
      "selectedCompetences": isUnifiedTest
        ? selectedCompetencesForPayload(competencyMode, selectedCompetences)
        : undefined
    }, (res) => {
      if (res === 500) {
        toast.error('Error al enviar una o más invitaciones');
      }
      if (res === 401) {
        toast.error(ERROR_MESSAGES.session_expired);
        refreshToken();
      }
      if (res[0]?.status === 'creado, en proceso de envío') {
        setSend(true);
        onTestSent?.();
      } else if (res !== 500 && res[0]?.status !== 'creado, en proceso de envío') {
        toast.error(res[0]?.status || 'Error al enviar una o más invitaciones');
        setSend(false);
      }
    });
  }

  const invitationInfo = () => {
    if (checkedItems.length === allItems.length || checkedItems.length === 0) {
      return invitationInfoText('todos los colaboradores.');
    }
    if (checkedItems.length > 0) {
      return invitationInfoText(`${map(checkedItems || [], (e) => ` ${e?.name || ''} ${e.surname || ''}`)}.`);
    }
  };
  // Ancho fijo desde que abre: si dependiera del modo elegido, el modal cambiaría de
  // tamaño bajo el cursor al marcar "competencias específicas".
  return (
    <Modal isOpen={open} toggle={toggle} className='gm-invitation-modal-wide'>
      <div className='gm-modal-header'>
        <div className='gm-modal-title-container'>
          <div className='gm-modal-title'>{ send ? 'Invitación Enviada' : 'Seleccione el modo de evaluación' }</div>
        </div>
        {!send && <button type="button" className="btn-close" aria-label="Close" onClick={toggle} />}
      </div>
      <ModalBody>
        <form onSubmit={handleSubmit}>
          <div className='gm-modal-form-container'>
            <div className='gm-invitation-container'>
              {send ?
                <div className='gm-invitation-done'>
                  <BiCheckCircle />
                  <div>{invitationSendText}</div >
                </div> :
                <div className='gm-test-options'>
                  {SHOW_TEST_SELECTION && <>
                  <p className='gm-invitation-info'>{invitationInfo()}</p>
                  <p className='gm-invitation-info'>Test disponibles a seleccionar:</p>
                  {
                    AVAILABLE_TESTS.filter(test => {
                      return TestTypeService.getTestConfig(test.code)?.rendering.showInInvitation;
                    }).map((test) => {
                      const testConfig = TestTypeService.getTestConfig(test.code);
                      const isInvitationDisabled = !testConfig?.acceptingInvitations;
                      return (
                        <label
                          key={test.id}
                          className={isInvitationDisabled ? 'gm-test-option-disabled' : ''}
                        >
                          {testConfig.name}
                          <input
                            className="input-checkbox"
                            type="checkbox"
                            disabled={isInvitationDisabled}
                            checked={targetTest === test.id}
                            onChange={() => handleSelectTest(test.id)}
                            value={targetTest === test.id}
                          />
                        </label>
                      );
                    })
                  }
                  </>}
                  {isUnifiedTest && (
                    <CompetencyModeSelector
                      heading={null}
                      mode={competencyMode}
                      onModeChange={setCompetencyMode}
                      selected={selectedCompetences}
                      onSelectedChange={setSelectedCompetences}
                    />
                  )}
                </div>
              }
            </div>
          </div>
          <div className='gm-modal-buttons'>
            {!send &&
              <>
                {/* Sin esto, el botón deshabilitado no dice qué falta para poder enviar. */}
                {isUnifiedTest && competencyMode === null && (
                  <span className='gm-competency-mode-hint' data-testid='competency-mode-hint'>
                    Selecciona un modo de evaluación para continuar
                  </span>
                )}
                <button type='button' className='gm-secondary-button' onClick={toggle} disabled={invitationResolver.loading}>
                  Cancelar
                </button>
                <ButtonLoading
                  className="gm-primary-button"
                  type="submit"
                  buttonLabel='Confirmar'
                  disabled={
                    invitationResolver.loading ||
                    (isUnifiedTest && !isCompetencySelectionValid(competencyMode, selectedCompetences))
                  }
                  loading={invitationResolver.loading}
                />
              </>
            }
            {send && <button type='button' className='gm-primary-button' onClick={handleToggle}>
              Confirmar
            </button>}
          </div>
        </form>
      </ModalBody>
    </Modal>
  );
}

export default InvitationModal;
